import React, { useState } from 'react';
import { C, BORDER_STYLES } from '../constants.jsx';

// ─── BASE STYLES ─────────────────────────────────────────────────────────────
const base = {
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const iSt = {
  ...base,
  display: 'block', width: '100%',
  padding: '6px 10px', fontSize: 12,
  border: `1px solid ${C.border}`,
  borderRadius: 7, color: C.text,
  background: C.surface, outline: 'none',
  transition: 'border-color .15s',
};

// ─── TOGGLE ──────────────────────────────────────────────────────────────────
export function Toggle({ value, onChange, size = 'md' }) {
  const dims = size === 'sm'
    ? { w: 28, h: 16, knob: 12 }
    : { w: 34, h: 20, knob: 16 };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();     // ✅ prevent parent interference
        e.preventDefault();      // ✅ extra safety
        onChange?.(!value);
      }}
      style={{
        width: dims.w,
        height: dims.h,
        borderRadius: dims.h,
        background: value ? '#4f46e5' : '#cbd5e1',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: dims.knob,
          height: dims.knob,
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: (dims.h - dims.knob) / 2,
          left: value ? dims.w - dims.knob - 2 : 2,
          transition: 'all 0.2s ease',
        }}
      />
    </div>
  );
}
// ─── TOGGLE ROW ───────────────────────────────────────────────────────────────
export function TogRow({ label, value, onChange, hint }) {
  return (
    <div
      onClick={(e) => e.stopPropagation()}  // ✅ isolate row
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
        gap: 10,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12 }}>{label}</div>
        {hint && (
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>
            {hint}
          </div>
        )}
      </div>

      <Toggle value={!!value} onChange={onChange} />
    </div>
  );
}

// ─── SECTION HEADING ─────────────────────────────────────────────────────────
export function SH({ children, action }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      fontSize: 10, fontWeight: 700, color: C.sub,
      letterSpacing: '.07em', textTransform: 'uppercase',
      margin: '16px 0 10px',
      paddingBottom: 7, borderBottom: `1px solid ${C.border}`,
    }}>
      <span>{children}</span>
      {action}
    </div>
  );
}

export function HR() {
  return <div style={{ height: 1, background: C.border, margin: '14px 0' }} />;
}

// ─── COLLAPSIBLE SECTION ──────────────────────────────────────────────────────
export function Collapsible({ title, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ marginBottom: 6 }}>
      
      {/* Header ONLY toggles */}
      <div
        onClick={(e) => {
          e.stopPropagation();   // ✅ prevent outer interference
          setOpen((v) => !v);
        }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          padding: '6px 8px',
          fontSize: 11,
          fontWeight: 600,
          background: '#f1f5f9',
          borderRadius: 6,
        }}
      >
        <span>{title}</span>
        {badge && <span>{badge}</span>}
      </div>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()} // ✅ isolate content
          style={{ marginTop: 6 }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ─── COLOR PICKER ─────────────────────────────────────────────────────────────
export function ColorRow({ label, value, onChange, hint }) {
  const safe = value || '#ffffff';
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{label}</label>
        {hint && <span style={{ fontSize: 11, color: C.muted }}>{hint}</span>}
      </div>
      <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
        <label style={{
          width: 36, height: 32, borderRadius: 7,
          background: safe,
          border: `1px solid ${C.borderMd}`,
          cursor: 'pointer', position: 'relative',
          flexShrink: 0, overflow: 'hidden', display: 'block',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.06)',
        }}>
          <input
            type="color" value={safe}
            onChange={(e) => onChange(e.target.value)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
          />
        </label>
        <input
          style={{ ...iSt, flex: 1, fontFamily: 'monospace', fontSize: 11 }}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#rrggbb or rgba(…)"
        />
        {value && value !== '#ffffff' && (
          <button
            onClick={() => onChange('')}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, padding: '0 4px', lineHeight: 1 }}
            title="Clear"
          >×</button>
        )}
      </div>
    </div>
  );
}

// ─── FONT SIZE + WEIGHT COMBINED ROW ─────────────────────────────────────────
export function FontRow({ sizeValue, weightValue, onSizeChange, onWeightChange }) {
  const weights = [300, 400, 500, 600, 700];
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
      {/* Size with slider */}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.05em' }}>Size</label>
          <span style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>{sizeValue || 14}px</span>
        </div>
        <input
          type="range" min={8} max={72} step={1} value={sizeValue || 14}
          onChange={(e) => onSizeChange(+e.target.value)}
          style={{ width: '100%', accentColor: C.accent }}
        />
      </div>
      {/* Weight icon buttons */}
      <div style={{ flexShrink: 0 }}>
        <label style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 4 }}>Weight</label>
        <div style={{ display: 'flex', gap: 3 }}>
          {weights.map((w) => (
            <button
              key={w}
              onClick={() => onWeightChange(w)}
              style={{
                width: 28, height: 26, border: `1px solid ${weightValue === w ? C.accent : C.border}`,
                borderRadius: 5, background: weightValue === w ? C.accentLt : C.surface,
                color: weightValue === w ? C.accent : C.sub,
                cursor: 'pointer', fontSize: 10, fontWeight: w,
                fontFamily: 'inherit',
              }}
            >{w === 300 ? 'L' : w === 400 ? 'R' : w === 500 ? 'M' : w === 600 ? 'SB' : 'B'}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ALIGN BUTTONS (text align / justify) ────────────────────────────────────
export function AlignButtons({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {['left', 'center', 'right'].map(v => (
        <button
          key={v}
          onClick={() => onChange(v)}
          style={{
            flex: 1,
            border: value === v ? '2px solid #2563EB' : '1px solid #E2E5EA',
            background: value === v ? '#EFF4FF' : '#fff'
          }}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

// ─── TEXT INPUT ───────────────────────────────────────────────────────────────
export function TextInput({ label, value, onChange, placeholder = '', hint, mono = false }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{label}</label>
        {hint && <span style={{ fontSize: 11, color: C.muted }}>{hint}</span>}
      </div>
      <input
        style={{ ...iSt, fontFamily: mono ? 'monospace' : 'inherit' }}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={(e) => (e.target.style.borderColor = C.accent)}
        onBlur={(e) => (e.target.style.borderColor = C.border)}
      />
    </div>
  );
}

// ─── NUMBER INPUT ─────────────────────────────────────────────────────────────
export function NumberInput({ label, value, onChange, min = 0, max, step = 1, unit, allowEmpty = false, placeholder = '' }) {
  return (
    <div>
      <label style={{ fontSize: 10, color: C.sub, display: 'block', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.05em' }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          style={{ ...iSt, paddingRight: unit ? 28 : 10 }}
          type="number"
          value={allowEmpty && (value === '' || value == null) ? '' : (value ?? 0)}
          min={min} max={max} step={step}
          placeholder={placeholder}
          onChange={(e) => onChange(allowEmpty && e.target.value === '' ? '' : +e.target.value)}
          onFocus={(e) => (e.target.style.borderColor = C.accent)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
        {unit && (
          <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: C.muted, pointerEvents: 'none' }}>
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── SLIDER ──────────────────────────────────────────────────────────────────
export function Slider({ label, value, onChange, min = 0, max = 100, step = 1, unit = '' }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{label}</label>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.accent }}>{value ?? 0}{unit}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="range" min={min} max={max} step={step} value={value ?? 0}
          onChange={(e) => onChange(+e.target.value)}
          style={{ flex: 1, accentColor: C.accent }}
        />
        <input
          type="number" min={min} max={max} step={step} value={value ?? 0}
          onChange={(e) => onChange(+e.target.value)}
          style={{ ...iSt, width: 52, textAlign: 'center', padding: '4px 6px', flexShrink: 0 }}
        />
      </div>
    </div>
  );
}

// ─── SELECT ──────────────────────────────────────────────────────────────────
export function Select({ label, value, onChange, options, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{label}</label>
        {hint && <span style={{ fontSize: 11, color: C.muted }}>{hint}</span>}
      </div>
      <div style={{ position: 'relative' }}>
        <select
          style={{
            ...iSt, cursor: 'pointer', appearance: 'none',
            paddingRight: 28, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236B7280'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
          }}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => (e.target.style.borderColor = C.accent)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        >
          {options.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ─── SEGMENTED CONTROL ────────────────────────────────────────────────────────
export function Segmented({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: C.text, display: 'block', marginBottom: 6 }}>{label}</label>}
      <div style={{
        display: 'flex', background: C.bg,
        border: `1px solid ${C.border}`, borderRadius: 8, padding: 3, gap: 2,
      }}>
        {options.map(([v, l]) => (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              flex: 1, padding: '5px 0', fontSize: 12, fontWeight: 500,
              border: 'none', borderRadius: 6, cursor: 'pointer',
              background: value === v ? C.surface : 'transparent',
              color: value === v ? C.text : C.sub,
              boxShadow: value === v ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
              transition: 'all .15s', fontFamily: 'inherit',
            }}
          >{l}</button>
        ))}
      </div>
    </div>
  );
}

// ─── ICON BUTTON GROUP ────────────────────────────────────────────────────────
export function IconGroup({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: C.text, display: 'block', marginBottom: 6 }}>{label}</label>}
      <div style={{ display: 'flex', gap: 4 }}>
        {options.map(([v, icon, tip]) => (
          <button
            key={v}
            title={tip || v}
            onClick={() => onChange(v)}
            style={{
              flex: 1, height: 34,
              border: `1px solid ${value === v ? C.accent : C.border}`,
              borderRadius: 6,
              background: value === v ? C.accentLt : C.surface,
              color: value === v ? C.accent : C.sub,
              cursor: 'pointer', fontSize: 15,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all .15s',
            }}
          >{icon}</button>
        ))}
      </div>
    </div>
  );
}

// ─── ROW2 / ROW3 / ROW4 ──────────────────────────────────────────────────────
export function Row2({ children, gap = 9, mb = 11 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap, marginBottom: mb }}>
      {children}
    </div>
  );
}
export function Row3({ children, gap = 7, mb = 11 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap, marginBottom: mb }}>
      {children}
    </div>
  );
}
export function Row4({ children, gap = 6, mb = 11 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap, marginBottom: mb }}>
      {children}
    </div>
  );
}

// ─── BORDER SECTION ──────────────────────────────────────────────────────────
export function BorderSection({ comp, onChange }) {
  return (
    <Collapsible title="Border & radius" defaultOpen={false}>
      <Row3 mb={10}>
        <NumberInput label="Width" value={comp.borderWidth} onChange={(v) => onChange({ borderWidth: v })} min={0} max={20} unit="px" />
        <div>
          <label style={{ fontSize: 10, color: C.sub, display: 'block', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.05em' }}>Style</label>
          <select
            style={{
              ...iSt, cursor: 'pointer', appearance: 'none', fontSize: 11,
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%236B7280'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 7px center', paddingRight: 22,
            }}
            value={comp.borderStyle || 'solid'}
            onChange={(e) => onChange({ borderStyle: e.target.value })}
          >
            {BORDER_STYLES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <NumberInput label="Radius" value={comp.borderRadius} onChange={(v) => onChange({ borderRadius: v })} min={0} max={64} unit="px" />
      </Row3>
      <ColorRow label="Border colour" value={comp.borderColor} onChange={(v) => onChange({ borderColor: v })} />
    </Collapsible>
  );
}

// ─── PADDING SECTION ─────────────────────────────────────────────────────────
export function PaddingSection({ comp, onChange }) {
  return (
    <Collapsible title="Padding" defaultOpen={false}>
      {/* Visual padding editor */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginBottom: 10 }}>
        <input
          type="number" value={comp.padT ?? 0} min={0}
          onChange={(e) => onChange({ padT: +e.target.value })}
          style={{ ...iSt, width: 64, textAlign: 'center', fontSize: 12 }}
          placeholder="T"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="number" value={comp.padL ?? 0} min={0}
            onChange={(e) => onChange({ padL: +e.target.value })}
            style={{ ...iSt, width: 64, textAlign: 'center', fontSize: 12 }}
            placeholder="L"
          />
          <div style={{ width: 64, height: 40, border: `2px dashed ${C.border}`, borderRadius: 6 }} />
          <input
            type="number" value={comp.padR ?? 0} min={0}
            onChange={(e) => onChange({ padR: +e.target.value })}
            style={{ ...iSt, width: 64, textAlign: 'center', fontSize: 12 }}
            placeholder="R"
          />
        </div>
        <input
          type="number" value={comp.padB ?? 0} min={0}
          onChange={(e) => onChange({ padB: +e.target.value })}
          style={{ ...iSt, width: 64, textAlign: 'center', fontSize: 12 }}
          placeholder="B"
        />
      </div>
    </Collapsible>
  );
}

// ─── FB STYLE SECTION ─────────────────────────────────────────────────────────
export function FBStyleSection({ comp, onChange }) {
  return (
    <>
      <Collapsible title="Label style" defaultOpen={true}>
        <FontRow
          sizeValue={comp.labelFontSize}
          weightValue={comp.labelFontWeight}
          onSizeChange={(v) => onChange({ labelFontSize: v })}
          onWeightChange={(v) => onChange({ labelFontWeight: v })}
        />
        <ColorRow label="Label colour" value={comp.labelColor} onChange={(v) => onChange({ labelColor: v })} />
        <TogRow label="Show label" value={comp.showLabel !== false} onChange={(v) => onChange({ showLabel: v })} />
        <TogRow label="Required (*)" value={!!comp.isMandatory} onChange={(v) => onChange({ isMandatory: v })} />
        <TextInput label="Help text" value={comp.helpText} onChange={(v) => onChange({ helpText: v })} placeholder="Short hint below the field" />
        <Slider label="Label–input gap" value={comp.gap} onChange={(v) => onChange({ gap: v })} min={0} max={40} unit="px" />
      </Collapsible>

      <Collapsible title="Input box style" defaultOpen={true}>
        <ColorRow label="Background" value={comp.inputBg} onChange={(v) => onChange({ inputBg: v })} />
        <ColorRow label="Text colour" value={comp.inputColor} onChange={(v) => onChange({ inputColor: v })} />
        <ColorRow label="Placeholder colour" value={comp.placeholderColor} onChange={(v) => onChange({ placeholderColor: v })} />
        <FontRow
          sizeValue={comp.inputFontSize}
          weightValue={400}
          onSizeChange={(v) => onChange({ inputFontSize: v })}
          onWeightChange={() => {}}
        />
        <Row2>
          <Slider label="Pad V" value={comp.inputPaddingV} onChange={(v) => onChange({ inputPaddingV: v })} min={0} max={32} unit="px" />
          <Slider label="Pad H" value={comp.inputPaddingH} onChange={(v) => onChange({ inputPaddingH: v })} min={0} max={40} unit="px" />
        </Row2>
        <Slider label="Border radius" value={comp.inputBorderRadius} onChange={(v) => onChange({ inputBorderRadius: v })} min={0} max={24} unit="px" />
      </Collapsible>

      <Collapsible title="Input border" defaultOpen={false}>
        <Row2>
          <Slider label="Width" value={comp.inputBorderWidth} onChange={(v) => onChange({ inputBorderWidth: v })} min={0} max={8} unit="px" />
          <Select
            label="Style"
            value={comp.inputBorderStyle || 'solid'}
            onChange={(v) => onChange({ inputBorderStyle: v })}
            options={BORDER_STYLES.map((s) => [s, s.charAt(0).toUpperCase() + s.slice(1)])}
          />
        </Row2>
        <ColorRow label="Border colour" value={comp.inputBorderColor} onChange={(v) => onChange({ inputBorderColor: v })} />
      </Collapsible>
    </>
  );
}

// ─── TEXT STYLE SECTION (for text_block) ─────────────────────────────────────
export function TextStyleSection({ comp, onChange }) {
  return (
    <>
      <Collapsible title="Typography" defaultOpen={true}>
        <FontRow
          sizeValue={comp.fontSize}
          weightValue={comp.fontWeight}
          onSizeChange={(v) => onChange({ fontSize: v })}
          onWeightChange={(v) => onChange({ fontWeight: v })}
        />
        <ColorRow label="Text colour" value={comp.color} onChange={(v) => onChange({ color: v })} />

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: C.text, display: 'block', marginBottom: 6 }}>Align</label>
          <IconGroup
            value={comp.align || 'left'}
            onChange={(v) => onChange({ align: v })}
            options={[
              ['left', '⬛', 'Left'],
              ['center', '▬', 'Center'],
              ['right', '⬛', 'Right'],
              ['justify', '≡', 'Justify'],
            ]}
          />
        </div>

        <Row2>
          <Slider label="Line height" value={comp.lineHeight} onChange={(v) => onChange({ lineHeight: v })} min={0.8} max={3} step={0.05} />
          <Slider label="Letter spacing" value={comp.letterSpacing} onChange={(v) => onChange({ letterSpacing: v })} min={-5} max={20} step={0.5} unit="em" />
        </Row2>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: C.text, display: 'block', marginBottom: 6 }}>Style</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { key: 'italic', icon: 'I', style: { fontStyle: 'italic' }, label: 'Italic' },
              { key: 'underline', icon: 'U', style: { textDecoration: 'underline' }, label: 'Underline' },
              { key: 'strikethrough', icon: 'S', style: { textDecoration: 'line-through' }, label: 'Strikethrough' },
            ].map(({ key, icon, style: s, label }) => (
              <button
                key={key}
                title={label}
                onClick={() => onChange({ [key]: !comp[key] })}
                style={{
                  width: 36, height: 32, border: `1px solid ${comp[key] ? C.accent : C.border}`,
                  borderRadius: 6, background: comp[key] ? C.accentLt : C.surface,
                  color: comp[key] ? C.accent : C.sub,
                  cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
                  ...s,
                }}
              >{icon}</button>
            ))}
          </div>
        </div>

        <Select
          label="Vertical align"
          value={comp.verticalAlign || 'center'}
          onChange={(v) => onChange({ verticalAlign: v })}
          options={[['top', 'Top'], ['center', 'Middle'], ['bottom', 'Bottom']]}
        />
      </Collapsible>
    </>
  );
}

// ─── CHILD COMPONENT MAPPER ───────────────────────────────────────────────────
// This is the main fix — a prominent, easy-to-use cell→component assignment UI
export function ChildMapper({ cells, allComps, parentId, onUpdate, onAdd, onDelete, cellLabel = 'Cell' }) {
  return (
    <div>
      {cells.map((cell, i) => (
        <div
          key={cell.id || i}
          style={{
            border: `1px solid ${cell.compId ? C.accentMd : C.border}`,
            borderRadius: 8, marginBottom: 8, overflow: 'hidden',
            background: cell.compId ? C.accentLt : C.surface,
          }}
        >
          {/* Cell header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 10px',
            borderBottom: `1px solid ${cell.compId ? C.accentMd : C.border}`,
            background: cell.compId ? `${C.accent}10` : C.bg,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{
                width: 20, height: 20, borderRadius: 5,
                background: cell.compId ? C.accent : C.borderMd,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0,
              }}>{i + 1}</div>
              <span style={{ fontSize: 12, fontWeight: 600, color: cell.compId ? C.accent : C.sub }}>
                {cell.label || `${cellLabel} ${i + 1}`}
              </span>
              {cell.compId && (
                <span style={{
                  fontSize: 10, padding: '2px 7px', borderRadius: 10,
                  background: C.accent, color: '#fff', fontWeight: 600,
                }}>
                  {allComps.find((x) => x.id === cell.compId)?.type || 'assigned'}
                </span>
              )}
            </div>
            <button
              onClick={() => onDelete(i)}
              style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
            >×</button>
          </div>

          {/* Cell body */}
          <div style={{ padding: '10px 10px 6px' }}>
            {/* Label */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 10, color: C.sub, display: 'block', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.05em' }}>
                Cell label
              </label>
              <input
                style={{ ...iSt, fontSize: 12 }}
                value={cell.label || ''}
                placeholder={`${cellLabel} ${i + 1}`}
                onChange={(e) => onUpdate(i, { label: e.target.value })}
              />
            </div>

            {/* Component assignment — the main action */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 10, color: C.sub, display: 'block', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.05em' }}>
                Assign component
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  style={{
                    ...iSt, cursor: 'pointer', appearance: 'none',
                    background: cell.compId ? C.accentLt : C.surface,
                    borderColor: cell.compId ? C.accent : C.border,
                    color: cell.compId ? C.accent : C.text,
                    fontWeight: cell.compId ? 600 : 400, fontSize: 12,
                    paddingRight: 28,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%236B7280'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
                  }}
                  value={cell.compId || ''}
                  onChange={(e) => onUpdate(i, { compId: e.target.value || null })}
                >
                  <option value="">— none (empty cell) —</option>
                  {allComps
                    .filter((x) => x.id !== parentId)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.type}  ·  {x.id}
                      </option>
                    ))}
                </select>
              </div>
              {cell.compId && (
                <button
                  onClick={() => onUpdate(i, { compId: null })}
                  style={{
                    marginTop: 5, width: '100%', padding: '5px', fontSize: 11,
                    border: `1px solid ${C.border}`, borderRadius: 6,
                    background: 'none', color: C.red, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  ✕ Remove assignment
                </button>
              )}
            </div>

            {/* Per-cell style */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              <div>
                <label style={{ fontSize: 10, color: C.sub, display: 'block', marginBottom: 3, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.05em' }}>Background</label>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <label style={{ width: 28, height: 26, borderRadius: 5, background: cell.bg || '#F7F8FA', border: `1px solid ${C.borderMd}`, cursor: 'pointer', position: 'relative', flexShrink: 0, overflow: 'hidden', display: 'block' }}>
                    <input type="color" value={cell.bg || '#F7F8FA'} onChange={(e) => onUpdate(i, { bg: e.target.value })} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                  </label>
                  <input style={{ ...iSt, flex: 1, fontSize: 10, fontFamily: 'monospace', padding: '5px 6px' }} value={cell.bg || ''} placeholder="transparent" onChange={(e) => onUpdate(i, { bg: e.target.value })} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 10, color: C.sub, display: 'block', marginBottom: 3, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.05em' }}>Border clr</label>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <label style={{ width: 28, height: 26, borderRadius: 5, background: cell.borderColor || '#CDD0D8', border: `1px solid ${C.borderMd}`, cursor: 'pointer', position: 'relative', flexShrink: 0, overflow: 'hidden', display: 'block' }}>
                    <input type="color" value={cell.borderColor || '#CDD0D8'} onChange={(e) => onUpdate(i, { borderColor: e.target.value })} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                  </label>
                  <input style={{ ...iSt, flex: 1, fontSize: 10, fontFamily: 'monospace', padding: '5px 6px' }} value={cell.borderColor || ''} placeholder="#CDD0D8" onChange={(e) => onUpdate(i, { borderColor: e.target.value })} />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginTop: 6 }}>
              <NumberInput label="Radius" value={cell.borderRadius} onChange={(v) => onUpdate(i, { borderRadius: v })} min={0} max={32} unit="px" />
              <NumberInput label="Padding" value={cell.padding} onChange={(v) => onUpdate(i, { padding: v })} min={0} max={64} unit="px" />
            </div>
          </div>
        </div>
      ))}

      {/* Add cell button */}
      <button
        onClick={onAdd}
        style={{
          width: '100%', padding: '10px', fontSize: 13, fontWeight: 600,
          border: `2px dashed ${C.accentMd}`, borderRadius: 8,
          background: C.accentLt, color: C.accent, cursor: 'pointer',
          fontFamily: 'inherit', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 6, transition: 'background .15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = C.accentMd)}
        onMouseLeave={(e) => (e.currentTarget.style.background = C.accentLt)}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
        Add {cellLabel}
      </button>
    </div>
  );
}

// ─── ADD BUTTON ───────────────────────────────────────────────────────────────
export function AddButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '9px', fontSize: 12, fontWeight: 600,
        border: `1px dashed ${C.accentMd}`, borderRadius: 7,
        background: C.accentLt, color: C.accent, cursor: 'pointer',
        fontFamily: 'inherit', marginTop: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        transition: 'background .15s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.accentMd)}
      onMouseLeave={(e) => (e.currentTarget.style.background = C.accentLt)}
    >
      <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
      {children}
    </button>
  );
}

// ─── CELL SLOT (legacy, kept for compatibility) ────────────────────────────────
export function CellSlot({ title, onDelete, children }) {
  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 8, padding: 10,
      marginBottom: 6, background: C.bg,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 11, fontWeight: 600, color: C.sub, marginBottom: 8,
      }}>
        {title}
        <button onClick={onDelete} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
          onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
        >×</button>
      </div>
      {children}
    </div>
  );
}
